import {getCodeLabel} from './dictionary';
import type {Section, SectionGroup} from './sections';
import type {EdiElementValue, EdiSegment} from './tokenizer';

function text(value: EdiElementValue | undefined): string {
  return typeof value === 'string' ? value : '';
}

function find(group: SectionGroup, id: string): EdiSegment | undefined {
  return group.entries.find(e => e.segment.id === id)?.segment;
}

function plural(count: number, noun: string, pluralNoun = `${noun}s`): string {
  return `${count} ${count === 1 ? noun : pluralNoun}`;
}

/** The one-line summary shown beside a section's title while collapsed. */
export function sectionPreview(section: Section): string {
  const entries = section.groups.flatMap(g => g.entries);
  switch (section.key) {
    case 'order': {
      const beg = find(section.groups[0], 'BEG');
      if (beg) {
        const po = text(beg.elements[2]);
        return po ? `PO ${po}` : '';
      }
      const bct = text(find(section.groups[0], 'BCT')?.elements[2]);
      if (bct) return `Report ${bct}`;
      const bak = find(section.groups[0], 'BAK');
      const ackType = text(bak?.elements[1]);
      if (ackType) return `Ack: ${getCodeLabel('BAK', 2, ackType)}`;
      const bqt = find(section.groups[0], 'BQT');
      const rfq = text(bqt?.elements[1]);
      if (rfq) return `RFQ ${rfq}`;
      const bqr = find(section.groups[0], 'BQR');
      const quoteNumber = text(bqr?.elements[1]);
      if (quoteNumber) return `Quote ${quoteNumber}`;
      const big = find(section.groups[0], 'BIG');
      const invoiceNumber = text(big?.elements[1]);
      if (invoiceNumber) return `Invoice ${invoiceNumber}`;
      const b3 = find(section.groups[0], 'B3');
      const b3Invoice = text(b3?.elements[1]);
      return b3Invoice ? `Invoice ${b3Invoice}` : '';
    }
    case 'parties':
      return plural(section.groups.length, 'party', 'parties');
    case 'stops':
      return plural(section.groups.length, 'stop');
    case 'items':
      return plural(section.groups.length, 'line');
    case 'reportDetails': {
      const bpt = find(section.groups[0], 'BPT');
      const reportNumber = text(bpt?.elements[1]);
      return reportNumber ? `Report ${reportNumber}` : '';
    }
    case 'lines':
      return plural(section.groups.length, 'line');
    case 'dates':
      return plural(entries.length, 'date');
    case 'notes':
      return plural(entries.length, 'note');
    case 'shipmentDetails': {
      const bsn = find(section.groups[0], 'BSN');
      const shipmentId = text(bsn?.elements[1]);
      return shipmentId ? `Shipment ${shipmentId}` : '';
    }
    case 'totals': {
      const total = text(find(section.groups[0], 'AMT')?.elements[1]);
      if (total) return `Total ${total}`;
      const tds = text(find(section.groups[0], 'TDS')?.elements[0]);
      if (tds) return `Total ${tds}`;
      const itemCount = text(find(section.groups[0], 'CTT')?.elements[0]);
      if (itemCount) return plural(Number(itemCount) || 0, 'item');
      const l3 = text(find(section.groups[0], 'L3')?.elements[4]);
      return l3 ? `Total ${l3}` : '';
    }
    case 'shipments':
      return plural(section.groups.length, 'shipment');
    case 'acknowledgmentSummary': {
      const ak901 = text(find(section.groups[0], 'AK9')?.elements[0]);
      return ak901 ? getCodeLabel('AK9', 1, ak901) : '';
    }
    case 'transactionSetAcks':
      return plural(section.groups.length, 'transaction set');
    default:
      return plural(entries.length, 'entry', 'entries');
  }
}

export function partyTitle(group: SectionGroup): {
  title: string;
  preview: string;
} {
  const n1 = find(group, 'N1');
  const n4 = find(group, 'N4');
  const code = text(n1?.elements[0]);
  const place = [text(n4?.elements[0]), text(n4?.elements[1])]
    .filter(Boolean)
    .join(', ');
  return {
    title: code ? getCodeLabel('N1', 1, code) : 'Party',
    preview: [text(n1?.elements[1]), place].filter(Boolean).join(' · '),
  };
}

export function lineTitle(
  group: SectionGroup,
  index: number
): {title: string; preview: string} {
  const po1 = find(group, 'PO1');
  const qty = [text(po1?.elements[1]), text(po1?.elements[2])]
    .filter(Boolean)
    .join(' ');
  const price = text(po1?.elements[3]);
  return {
    title: `Line ${text(po1?.elements[0]) || index + 1}`,
    preview: [
      text(po1?.elements[6]),
      qty && `Qty ${qty}`,
      price && `@ ${price}`,
    ]
      .filter(Boolean)
      .join(' · '),
  };
}

export function ak2RowTitle(
  group: SectionGroup,
  index: number
): {title: string; preview: string} {
  const ak2 = find(group, 'AK2');
  const ak5 = find(group, 'AK5');
  const setId = text(ak2?.elements[0]);
  const controlNumber = text(ak2?.elements[1]);
  const ak501 = text(ak5?.elements[0]);
  return {
    title: setId
      ? `${setId}${controlNumber ? ` #${controlNumber}` : ''}`
      : `Transaction set ${index + 1}`,
    preview: ak501 ? getCodeLabel('AK5', 1, ak501) : '',
  };
}

export function ak3RowTitle(group: SectionGroup): {
  title: string;
  preview: string;
} {
  const ak3 = find(group, 'AK3');
  const segmentId = text(ak3?.elements[0]);
  const ak304 = text(ak3?.elements[3]);
  return {
    title: segmentId ? `${segmentId} segment` : 'Segment note',
    preview: ak304 ? getCodeLabel('AK3', 4, ak304) : '',
  };
}

/** The 852 equivalent of lineTitle — an item-activity (LIN) line's quantity
 * lives on its own QTY segment(s) rather than on LIN itself, so this reads
 * from the group's first QTY entry rather than from LIN's own elements. */
export function lineTitle852(
  group: SectionGroup,
  index: number
): {title: string; preview: string} {
  const lin = find(group, 'LIN');
  const qty = find(group, 'QTY');
  const qtyValue = text(qty?.elements[1]);
  const qtyUom = text(qty?.elements[2]);
  const qtyText = qtyValue
    ? `Qty ${qtyValue}${qtyUom ? ` ${qtyUom}` : ''}`
    : '';
  return {
    title: `Line ${text(lin?.elements[0]) || index + 1}`,
    preview: [text(lin?.elements[2]), qtyText].filter(Boolean).join(' · '),
  };
}

/** The 855 equivalent of lineTitle — the line's acknowledgment disposition
 * lives on the loop's ACK segment, so the preview surfaces ACK01's status
 * label alongside PO1's product id and quantity. */
export function lineTitle855(
  group: SectionGroup,
  index: number
): {title: string; preview: string} {
  const po1 = find(group, 'PO1');
  const ack = find(group, 'ACK');
  const qty = [text(po1?.elements[1]), text(po1?.elements[2])]
    .filter(Boolean)
    .join(' ');
  const ackCode = text(ack?.elements[0]);
  const status = ackCode ? getCodeLabel('ACK', 1, ackCode) : '';
  return {
    title: `Line ${text(po1?.elements[0]) || index + 1}`,
    preview: [text(po1?.elements[6]), qty && `Qty ${qty}`, status]
      .filter(Boolean)
      .join(' · '),
  };
}

/** The 840 equivalent of lineTitle — an RFQ line has no settled PO1 unit
 * price yet, so the preview reads the requested price from CTP (CTP02/
 * CTP03) instead, alongside PID's product description and PO1's own
 * quantity/UOM elements (same positions lineTitle reads). */
export function lineTitle840(
  group: SectionGroup,
  index: number
): {title: string; preview: string} {
  const po1 = find(group, 'PO1');
  const pid = find(group, 'PID');
  const ctp = find(group, 'CTP');
  const qty = [text(po1?.elements[1]), text(po1?.elements[2])]
    .filter(Boolean)
    .join(' ');
  const price = text(ctp?.elements[2]);
  return {
    title: `Line ${text(po1?.elements[0]) || index + 1}`,
    preview: [
      text(pid?.elements[4]),
      qty && `Qty ${qty}`,
      price && `Requested @ ${price}`,
    ]
      .filter(Boolean)
      .join(' · '),
  };
}

export function lineTitle843(
  group: SectionGroup,
  index: number
): {title: string; preview: string} {
  const po1 = find(group, 'PO1');
  const pid = find(group, 'PID');
  const ctp = find(group, 'CTP');
  const qty = [text(po1?.elements[1]), text(po1?.elements[2])]
    .filter(Boolean)
    .join(' ');
  const price = text(ctp?.elements[2]);
  return {
    title: `Line ${text(po1?.elements[0]) || index + 1}`,
    preview: [
      text(pid?.elements[4]),
      qty && `Qty ${qty}`,
      price && `Quoted @ ${price}`,
    ]
      .filter(Boolean)
      .join(' · '),
  };
}

/** The 844 equivalent of lineTitle — a claim/adjustment line's identifying
 * values live on its nested QTY/AMT segments rather than on PAD itself, so
 * this reads from the group's first QTY and AMT entries (mirroring
 * lineTitle852's pattern) alongside PAD02's Product Transfer Type Code
 * label as the title. */
export function lineTitle844(
  group: SectionGroup,
  index: number
): {title: string; preview: string} {
  const pad = find(group, 'PAD');
  const qty = find(group, 'QTY');
  const amt = find(group, 'AMT');
  const padType = text(pad?.elements[1]);
  const qtyValue = text(qty?.elements[1]);
  const amtValue = text(amt?.elements[1]);
  return {
    title: padType ? getCodeLabel('PAD', 2, padType) : `Line ${index + 1}`,
    preview: [amtValue && `Amount ${amtValue}`, qtyValue && `Qty ${qtyValue}`]
      .filter(Boolean)
      .join(' · '),
  };
}

/** The 845 equivalent of lineTitle — a price-authorization line's
 * identifying values live on its nested CTP/QTY segments rather than on
 * PAD itself, so this reads from the group's first CTP and QTY entries
 * (CTP03, the same position lineTitle840/lineTitle843 read for price)
 * alongside PAD02's Product Transfer Type Code label as the title. */
export function lineTitle845(
  group: SectionGroup,
  index: number
): {title: string; preview: string} {
  const pad = find(group, 'PAD');
  const ctp = find(group, 'CTP');
  const qty = find(group, 'QTY');
  const padType = text(pad?.elements[1]);
  const price = text(ctp?.elements[2]);
  const qtyValue = text(qty?.elements[1]);
  return {
    title: padType ? getCodeLabel('PAD', 2, padType) : `Line ${index + 1}`,
    preview: [price && `Price ${price}`, qtyValue && `Qty ${qtyValue}`]
      .filter(Boolean)
      .join(' · '),
  };
}

/** The 867 equivalent of lineTitle — a product-transfer/resale line's
 * identifying value is its own PTD01 Product Transfer Type Code, with the
 * item's nested REF02 and QTY02 as the preview (the product-transfer-type
 * label is already the row's title, so the preview only needs reference and
 * quantity, per the requirements doc's own preview example). */
export function lineTitle867(
  group: SectionGroup,
  index: number
): {title: string; preview: string} {
  const ptd = find(group, 'PTD');
  const ref = find(group, 'REF');
  const qty = find(group, 'QTY');
  const ptdType = text(ptd?.elements[0]);
  const refValue = text(ref?.elements[1]);
  const qtyValue = text(qty?.elements[1]);
  return {
    title: ptdType ? getCodeLabel('PTD', 1, ptdType) : `Line ${index + 1}`,
    preview: [refValue && `Ref ${refValue}`, qtyValue && `Qty ${qtyValue}`]
      .filter(Boolean)
      .join(' · '),
  };
}

/** The 810 equivalent of lineTitle — an invoice line's product/description
 * prefers PID's description, falling back to IT1's own product/service id,
 * alongside IT1's own quantity/UOM/unit-price elements (same positions
 * lineTitle reads from PO1). */
export function lineTitle810(
  group: SectionGroup,
  index: number
): {title: string; preview: string} {
  const it1 = find(group, 'IT1');
  const pid = find(group, 'PID');
  const qty = [text(it1?.elements[1]), text(it1?.elements[2])]
    .filter(Boolean)
    .join(' ');
  const price = text(it1?.elements[3]);
  return {
    title: `Line ${text(it1?.elements[0]) || index + 1}`,
    preview: [
      text(pid?.elements[4]) || text(it1?.elements[6]),
      qty && `Qty ${qty}`,
      price && `@ ${price}`,
    ]
      .filter(Boolean)
      .join(' · '),
  };
}

/** The 210's Stops panel row — a stop's identifying value is its own Name
 * loop's N101 Entity Identifier Code (e.g. Shipper, Consignee), falling
 * back to "Stop N"; the preview is the stop's own G62 date and/or its
 * party's N4 city, mirroring lineTitle845's shape above. */
export function lineTitle210(
  group: SectionGroup,
  index: number
): {title: string; preview: string} {
  const n1 = find(group, 'N1');
  const n4 = find(group, 'N4');
  const g62 = find(group, 'G62');
  const code = text(n1?.elements[0]);
  const city = text(n4?.elements[0]);
  const date = text(g62?.elements[1]);
  return {
    title: code ? getCodeLabel('N1', 1, code) : `Stop ${index + 1}`,
    preview: [date, city].filter(Boolean).join(' · '),
  };
}

/** The 210's freight/accessorial charge-line row — the L1 segment's own
 * L108 Special Charge or Allowance Code as the title, the L104 charge
 * amount as the preview, mirroring ak3RowTitle's shape above. */
export function chargeLineTitle210(group: SectionGroup): {
  title: string;
  preview: string;
} {
  const l1 = find(group, 'L1');
  const code = text(l1?.elements[7]);
  const amount = text(l1?.elements[3]);
  return {
    title: code ? getCodeLabel('L1', 8, code) : 'Charge',
    preview: amount ? `$${amount}` : '',
  };
}
